<template>
  <div class="oh-page" v-loading="loading">

    <!-- Header -->
    <div class="oh-header">
      <div class="oh-inner oh-header-inner">
        <h1 class="oh-title">Order History</h1>
        <div class="oh-header-actions">
          <button class="oh-btn oh-btn--purple" @click="router.push('/request')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
            New Order
          </button>
          <button class="oh-btn oh-btn--csv" @click="downloadExport('csv')" :disabled="exporting === 'csv'">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><polyline points="8 17 12 21 16 17"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"/></svg>
            {{ exporting === 'csv' ? 'Preparing…' : 'CSV' }}
          </button>
          <button class="oh-btn oh-btn--pdf" @click="downloadExport('pdf')" :disabled="exporting === 'pdf'">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/></svg>
            {{ exporting === 'pdf' ? 'Preparing…' : 'PDF' }}
          </button>
        </div>
      </div>
    </div>

    <div class="oh-inner oh-list-wrap">

      <!-- Filters -->
      <div class="oh-filters">
        <div class="oh-filter" v-if="approvalEnabled">
          <label class="oh-filter-lbl">Approval</label>
          <select class="oh-select" v-model="approvalFilter" @change="onFilterChange">
            <option value="">All</option>
            <option value="Pending">Pending Approval</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
        <div class="oh-filter">
          <label class="oh-filter-lbl">Delivery status</label>
          <select class="oh-select" v-model="deliveryFilter" @change="onFilterChange">
            <option value="">All</option>
            <option value="Placed">Placed</option>
            <option value="Intransit">In transit</option>
            <option value="Delivered">Delivered</option>
            <!-- No "Invoiced": it was an order status set by a TMS button that is
                 now retired, and no order has it, so it only ever showed nothing.
                 Whether an order is billed is on the Invoices page. -->
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        <!-- Date range: presets first, because almost nobody wants to pick two
             arbitrary days -- they want this month, or last month. -->
        <div class="oh-filter oh-filter--date">
          <label class="oh-filter-lbl">Date placed</label>
          <button
            type="button"
            class="oh-select oh-datebtn"
            :class="{ 'oh-datebtn--on': dateActive }"
            :aria-expanded="dateOpen"
            @click.stop="dateOpen = !dateOpen"
          >
            <span class="oh-datebtn-txt">{{ dateLabel }}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>

          <div v-if="dateOpen" class="oh-datepop" @click.stop>
            <div class="oh-datepop-presets">
              <button
                v-for="p in datePresets"
                :key="p.key"
                type="button"
                class="oh-preset"
                :class="{ 'oh-preset--on': activePreset === p.key }"
                @click="applyPreset(p)"
              >{{ p.label }}</button>
            </div>
            <div class="oh-datepop-custom">
              <p class="oh-datepop-hd">Custom range</p>
              <label class="oh-datepop-lbl">From</label>
              <input type="date" class="oh-dateinput" v-model="customFrom" :max="today">
              <label class="oh-datepop-lbl">To</label>
              <input type="date" class="oh-dateinput" v-model="customTo" :max="today">
              <div class="oh-datepop-actions">
                <button type="button" class="oh-datepop-btn" @click="clearDates">Clear</button>
                <button
                  type="button"
                  class="oh-datepop-btn oh-datepop-btn--go"
                  :disabled="!customFrom && !customTo"
                  @click="applyCustom"
                >Apply</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="oh-table" :class="{ 'oh-has-approval': approvalEnabled }">

        <!-- Column headers -->
        <div class="oh-thead">
          <div class="oh-th oh-th--order">ORDER</div>
          <div class="oh-th oh-th--status">STATUS</div>
          <div class="oh-th oh-th--appr" v-if="approvalEnabled">APPROVAL</div>
          <div class="oh-th oh-th--user">USER</div>
          <div class="oh-th oh-th--date">DATE</div>
          <div class="oh-th oh-th--amount">AMOUNT</div>
          <div class="oh-th oh-th--from">FROM</div>
          <div class="oh-th oh-th--to">TO</div>
          <div class="oh-th oh-th--veh">VEHICLE</div>
          <div class="oh-th oh-th--action"></div>
        </div>

        <!-- Rows -->
        <div class="oh-tbody">
          <div
            v-for="row in tableData.data"
            :key="row.id"
            class="oh-row-wrap"
            :class="{ 'oh-row-wrap--open': expandedRows.has(row.id) }"
          >
            <div class="oh-row" @click="toggleRow(row)">
              <div class="oh-td oh-td--order">
                <span class="oh-ref">{{ fmtOrderNum(row.order_number) }}</span>
              </div>
              <div class="oh-td oh-td--status">
                <span class="oh-badge" :class="getStatusClass(row.order_status_text)">{{ statusLabel(row.order_status_text) }}</span>
              </div>
              <div class="oh-td oh-td--appr" v-if="approvalEnabled">
                <span v-if="row.customer_approval_status" class="oh-appr-pill" :class="approvalClass(row.customer_approval_status)">{{ row.customer_approval_status }}</span>
                <span v-else class="oh-td-muted">—</span>
              </div>
              <div class="oh-td oh-td--user">
                <span class="oh-td-text">{{ row.user_name }}</span>
              </div>
              <div class="oh-td oh-td--date">
                <span class="oh-td-muted">{{ transformDateSQL(row.scheduled_time) }}</span>
              </div>
              <div class="oh-td oh-td--amount">
                <span class="oh-td-amount">KES {{ row.cost != null ? Math.round(row.cost).toLocaleString() : '—' }}</span>
              </div>
              <div class="oh-td oh-td--from">
                <span class="oh-td-muted">{{ row.pickup }}</span>
              </div>
              <div class="oh-td oh-td--to">
                <span class="oh-td-muted">{{ row.dropoff }}</span>
              </div>
              <!-- Which truck came. Blank until ops assign a driver, which is
                   most of an order's early life, so the dash is the normal case. -->
              <div class="oh-td oh-td--veh">
                <template v-if="row.plate_number || row.vehicle_type">
                  <span class="oh-plate" v-if="row.plate_number">{{ row.plate_number }}</span>
                  <span class="oh-veh-type" v-if="row.vehicle_type">{{ row.vehicle_type }}</span>
                </template>
                <span v-else class="oh-td-muted" title="A vehicle is shown once a driver has been assigned.">—</span>
              </div>
              <div class="oh-td oh-td--action">
                <span class="oh-view-link">
                  {{ expandedRows.has(row.id) ? 'Hide' : 'View' }}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </span>
              </div>
            </div>

            <!-- Expanded detail -->
            <Transition name="expand">
              <div class="oh-detail" v-if="expandedRows.has(row.id)" v-loading="row.orderDetails == undefined">
                <div class="oh-detail-grid" v-if="row.orderDetails != undefined">

                  <!-- Order info card -->
                  <div class="oh-dcard">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--blue">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
                      </div>
                      <div class="oh-dcard-label">Order Info</div>
                      <span class="oh-badge" :class="getStatusClass(row.order_status_text)" style="margin-left:auto;font-size:0.68rem;padding:2px 9px;">{{ statusLabel(row.order_status_text) }}</span>
                    </div>
                    <div class="oh-dkv-list">
                      <div class="oh-dkv"><span class="oh-dk">Reference</span><span class="oh-dv" style="font-family:monospace;font-size:0.8rem;letter-spacing:0.04em;">{{ fmtOrderNum(row.order_number) }}</span></div>
                      <div class="oh-dkv"><span class="oh-dk">Amount</span><span class="oh-dv oh-dv--navy">KES {{ row.cost != null ? Math.round(row.cost).toLocaleString() : '—' }}</span></div>
                      <div class="oh-dkv"><span class="oh-dk">Scheduled</span><span class="oh-dv">{{ transformDateSQL(row.scheduled_time) }}</span></div>
                      <div class="oh-dkv" v-if="row.orderDetails.order?.placed_by"><span class="oh-dk">Requested by</span><span class="oh-dv">{{ row.orderDetails.order.placed_by }}</span></div>
                      <div class="oh-dkv" v-if="row.orderDetails.order?.delivery_logs?.find((log: any) => log.log_type === 'Intransit')">
                        <span class="oh-dk">Picked up</span>
                        <span class="oh-dv">{{ transformDate(row.orderDetails.order.delivery_logs.find((log: any) => log.log_type === 'Intransit')['log_time']) }}</span>
                      </div>
                      <div class="oh-dkv" v-if="row.orderDetails.order?.delivery_logs?.find((log: any) => log.log_type === 'Dropped')">
                        <span class="oh-dk">Delivered</span>
                        <span class="oh-dv oh-dv--green">{{ transformDate(row.orderDetails.order.delivery_logs.find((log: any) => log.log_type === 'Dropped')['log_time']) }}</span>
                      </div>
                    </div>

                    <!-- Customer approval -->
                    <div v-if="row.customer_approval_status" class="oh-appr-box" :class="'oh-appr-box--' + row.customer_approval_status.toLowerCase()">
                      <span class="oh-appr-pill" :class="approvalClass(row.customer_approval_status)">{{ approvalLabel(row.customer_approval_status) }}</span>

                      <!-- Conversation thread — the operations team on the left, you on the right -->
                      <div v-if="row.orderDetails?.order?.approval_log?.length" class="oh-appr-thread">
                        <div v-for="(m, i) in row.orderDetails.order.approval_log" :key="i"
                             class="oh-appr-msg" :class="m.actor_type === 'customer' ? 'oh-appr-msg--me' : 'oh-appr-msg--them'">
                          <div class="oh-appr-msg-head">
                            <span class="oh-appr-dot" :style="{ background: apprActionColor(m.action) }"></span>
                            <span class="oh-appr-who">{{ m.actor_type === 'customer' ? 'You' : m.actor }}</span>
                            <span class="oh-appr-act" :style="{ color: apprActionColor(m.action), background: apprActionColor(m.action) + '1a' }">{{ apprActionLabel(m.action) }}</span>
                          </div>
                          <p v-if="m.note" class="oh-appr-note">{{ m.note }}</p>
                          <p class="oh-appr-time">{{ fmtApprTime(m.created_at) }}</p>
                        </div>
                      </div>
                      <p v-else-if="row.customer_approval_status === 'Rejected' && row.customer_approval_reason" class="oh-appr-reason"><strong>Your reason:</strong> {{ row.customer_approval_reason }}</p>

                      <template v-if="row.customer_approval_status === 'Pending'">
                        <p class="oh-appr-hint">Please review this order — approve it, or reject it with a reason.</p>
                        <template v-if="!rejectMode.has(row.id)">
                          <div class="oh-appr-actions">
                            <button class="oh-appr-btn oh-appr-btn--approve" :disabled="acting.has(row.id)" @click.stop="approveOrder(row)">Approve</button>
                            <button class="oh-appr-btn oh-appr-btn--reject" :disabled="acting.has(row.id)" @click.stop="startReject(row)">Reject</button>
                          </div>
                        </template>
                        <template v-else>
                          <textarea v-model="rejectReason[row.id]" class="oh-appr-textarea" placeholder="Why are you rejecting this order?" rows="2" @click.stop></textarea>
                          <div class="oh-appr-actions">
                            <button class="oh-appr-btn oh-appr-btn--reject" :disabled="acting.has(row.id)" @click.stop="submitReject(row)">Submit rejection</button>
                            <button class="oh-appr-btn oh-appr-btn--ghost" :disabled="acting.has(row.id)" @click.stop="cancelReject(row)">Cancel</button>
                          </div>
                        </template>
                      </template>
                    </div>

                    <!-- Pricing breakdown -->
                    <div v-if="row.orderDetails.order?.pricing_model" class="oh-pricing">
                      <div class="oh-pricing-head">
                        <span class="oh-pricing-label">Pricing</span>
                        <span class="oh-pricing-model">{{ ({ distance: 'Distance', tonnage: 'Tonnage', location: 'Fixed Route' } as Record<string, string>)[row.orderDetails.order.pricing_model] || row.orderDetails.order.pricing_model }}</span>
                      </div>
                      <template v-if="row.orderDetails.order.pricing_snapshot?.breakdown">
                        <template v-if="row.orderDetails.order.pricing_model === 'distance'">
                          <div class="oh-pline"><span>Base fee ({{ row.orderDetails.order.pricing_snapshot.breakdown.base_distance }}km incl.)</span><span>KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.base_fee).toLocaleString() }}</span></div>
                          <div class="oh-pline" v-if="row.orderDetails.order.pricing_snapshot.breakdown.extra_km > 0"><span>{{ row.orderDetails.order.pricing_snapshot.breakdown.extra_km }}km extra × KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.cost_per_km).toLocaleString() }}/km</span><span>KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.extra_km_cost).toLocaleString() }}</span></div>
                          <div class="oh-pline" v-if="row.orderDetails.order.pricing_snapshot.breakdown.extra_stops > 0"><span>{{ row.orderDetails.order.pricing_snapshot.breakdown.extra_stops }} extra stop(s) × KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.cost_per_stop).toLocaleString() }}</span><span>KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.extra_stops_cost).toLocaleString() }}</span></div>
                          <div class="oh-pline" v-if="row.orderDetails.order.distance_km || row.orderDetails.order.distance_text"><span>Total distance</span><span>{{ formatDistance(row.orderDetails.order.distance_text, row.orderDetails.order.distance_km) }}</span></div>
                        </template>
                        <template v-else-if="row.orderDetails.order.pricing_model === 'tonnage'">
                          <div class="oh-pline"><span>Goods weight</span><span>{{ row.orderDetails.order.pricing_snapshot.breakdown.tonnes }} tonnes</span></div>
                          <div class="oh-pline"><span>{{ row.orderDetails.order.pricing_snapshot.breakdown.rounded_km }}km × {{ row.orderDetails.order.pricing_snapshot.breakdown.tonnes }}t × KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.tonnage_rate).toLocaleString() }}/t/km</span><span>KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.tonnage_cost).toLocaleString() }}</span></div>
                          <div class="oh-pline" v-if="row.orderDetails.order.pricing_snapshot.breakdown.extra_stops > 0"><span>{{ row.orderDetails.order.pricing_snapshot.breakdown.extra_stops }} extra stop(s)</span><span>KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.extra_stops_cost).toLocaleString() }}</span></div>
                        </template>
                        <template v-else-if="row.orderDetails.order.pricing_model === 'location'">
                          <div class="oh-pline" v-if="row.orderDetails.order.pricing_snapshot.breakdown.route_name"><span>Matched route</span><span>{{ row.orderDetails.order.pricing_snapshot.breakdown.route_name }}</span></div>
                          <div class="oh-pline"><span>Fixed price</span><span>KES {{ Math.round(row.orderDetails.order.pricing_snapshot.breakdown.fixed_price).toLocaleString() }}</span></div>
                        </template>
                        <div class="oh-pline oh-pline--total"><span>Total</span><span>KES {{ row.cost != null ? Math.round(row.cost).toLocaleString() : '—' }}</span></div>
                      </template>
                    </div>
                  </div>

                  <!-- Route card -->
                  <div class="oh-dcard oh-dcard--route">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--purple">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      </div>
                      <div class="oh-dcard-label">Route</div>
                    </div>

                    <!-- Stop list -->
                    <div class="oh-route-list">
                      <!-- Pickup -->
                      <div class="oh-ritem oh-ritem--pickup">
                        <div class="oh-ritem-left">
                          <div class="oh-ritem-dot oh-ritem-dot--green"></div>
                          <div class="oh-ritem-line" v-if="getRouteStops(row).length > 1"></div>
                        </div>
                        <div class="oh-ritem-body">
                          <div class="oh-ritem-tag">Pick-up</div>
                          <div class="oh-ritem-addr">{{ getRouteStops(row)[0]?.location || row.pickup }}</div>
                        </div>
                      </div>
                      <!-- Drop-off stops -->
                      <div
                        v-for="(stop, si) in getRouteStops(row).slice(1)"
                        :key="si"
                        class="oh-ritem"
                      >
                        <div class="oh-ritem-left">
                          <div class="oh-ritem-dot" :class="Number(si) === getRouteStops(row).length - 2 ? 'oh-ritem-dot--amber' : 'oh-ritem-dot--mid'"></div>
                          <div class="oh-ritem-line" v-if="Number(si) < getRouteStops(row).length - 2"></div>
                        </div>
                        <div class="oh-ritem-body">
                          <div class="oh-ritem-tag oh-ritem-tag--drop">
                            <span class="oh-ritem-num">{{ Number(si) + 1 }}</span>
                            Drop-off
                          </div>
                          <div class="oh-ritem-addr">{{ stop.location || row.dropoff }}</div>
                          <div v-if="stopDocuments(row, stop.id).length" class="oh-stopdocs">
                            <button
                              v-for="(doc, di) in stopDocuments(row, stop.id)" :key="di"
                              class="oh-doc-chip"
                              @click.stop="openDocModal(stopDocuments(row, stop.id), Number(di), row)"
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              <span>{{ doc.title || 'Document' }}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Distance row -->
                    <div class="oh-route-distance" v-if="row.orderDetails.order?.distance_text || row.orderDetails.order?.distance_km">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      <span class="oh-route-distance-label">Total distance</span>
                      <span class="oh-route-distance-value">{{ formatDistance(row.orderDetails.order?.distance_text, row.orderDetails.order?.distance_km) }}</span>
                    </div>

                    <!-- Footer: stops count + track link -->
                    <div class="oh-route-footer">
                      <span class="oh-route-stops-pill" v-if="getRouteStops(row).length > 1">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        {{ getRouteStops(row).length - 1 }} stop{{ getRouteStops(row).length - 1 !== 1 ? 's' : '' }}
                      </span>
                      <a :href="returnOrderTrackingLink(row.id)" class="oh-track-btn" @click.stop>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        Track order
                      </a>
                    </div>

                    <!-- Delivery documents now appear under each destination above. -->
                  </div>

                  <!-- Driver + documents column -->
                  <div class="oh-dcol">
                  <!-- Driver card -->
                  <div class="oh-dcard">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--teal">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      </div>
                      <div class="oh-dcard-label">Driver</div>
                    </div>
                    <div v-if="row.orderDetails.order?.driver_name">
                      <div class="oh-driver" style="margin-bottom:12px;">
                        <div class="oh-driver-avatar">{{ row.orderDetails.order.driver_name?.charAt(0)?.toUpperCase() }}</div>
                        <div>
                          <div class="oh-driver-name">{{ row.orderDetails.order.driver_name }}</div>
                          <div class="oh-driver-plate" v-if="row.orderDetails.order.plate_number">{{ row.orderDetails.order.plate_number }}</div>
                        </div>
                      </div>
                      <div class="oh-dkv-list">
                        <div class="oh-dkv" v-if="row.orderDetails.order.vehicle_type"><span class="oh-dk">Vehicle type</span><span class="oh-dv">{{ row.orderDetails.order.vehicle_type }}</span></div>
                        <div class="oh-dkv" v-if="row.orderDetails.order.partner_name"><span class="oh-dk">Partner</span><span class="oh-dv">{{ row.orderDetails.order.partner_name }}</span></div>
                        <div class="oh-dkv" v-if="row.orderDetails.order.driver_phone"><span class="oh-dk">Phone</span><span class="oh-dv">{{ row.orderDetails.order.driver_phone }}</span></div>
                      </div>
                    </div>
                    <div class="oh-driver-empty" v-else>
                      <div class="oh-driver-empty-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      </div>
                      <div class="oh-driver-empty-title">No driver assigned</div>
                      <div class="oh-driver-empty-sub">{{ ['Delivered','Invoiced'].includes(row.order_status_text) ? `This delivery was fulfilled directly by ${config.getCompanyName} operations.` : 'A driver will be assigned shortly.' }}</div>
                    </div>
                  </div>

                  <!-- Order Documents — its own card, clearly separate from the driver.
                       Shown when there is a manifest number even with no file attached:
                       for some clients the number IS the record. -->
                  <div class="oh-dcard" v-if="orderLevelDocuments(row).length || row.manifest_no">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--blue">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0061eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      </div>
                      <div class="oh-dcard-label">Order Documents</div>
                    </div>
                    <div class="oh-manifest" v-if="row.manifest_no">
                      <span class="oh-manifest-k">Manifest</span>
                      <span class="oh-manifest-v">{{ row.manifest_no }}</span>
                    </div>
                    <div class="oh-orderdocs-list">
                      <button
                        v-for="(doc, di) in orderLevelDocuments(row)" :key="di"
                        class="oh-doc-chip oh-doc-chip--wide"
                        @click.stop="openDocModal(orderLevelDocuments(row), Number(di), row)"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        <span>{{ doc.title || 'Document' }}</span>
                      </button>
                    </div>
                  </div>
                  </div><!-- /oh-dcol -->

                </div>
              </div>
            </Transition>
          </div>
        </div>

      </div>

      <!-- Pagination -->
      <div v-if="!loading && totalPages() > 1" class="oh-pagination">
        <button class="oh-page-btn" :disabled="page === 1" @click="changePage(page - 1)">&#8592; Prev</button>
        <span class="oh-page-info">Page {{ page }} of {{ totalPages() }}</span>
        <button class="oh-page-btn" :disabled="page >= totalPages()" @click="changePage(page + 1)">Next &#8594;</button>
      </div>

      <!-- Empty state -->
      <div class="oh-empty" v-if="!loading && tableData.data.length === 0">
        <div class="oh-empty-icon">📦</div>
        <div class="oh-empty-text">No orders yet</div>
        <button class="oh-btn oh-btn--navy" @click="router.push('/request')">Place your first order</button>
      </div>

    </div>

    <!-- Document Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="docModal.open" class="dm-backdrop" @click.self="closeDocModal">
          <div class="dm-modal">

            <!-- Header: title + nav + actions -->
            <div class="dm-header">
              <div class="dm-header-left">
                <div class="dm-header-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div class="dm-header-text">
                  <div class="dm-title">{{ docModal.docs[docModal.active]?.title || 'Document' }}</div>
                  <div class="dm-subtitle">
                    {{ docModal.orderNumber }}
                    <span v-if="docModal.docs[docModal.active]?.approved" class="dm-approved">Reviewed and approved</span>
                  </div>
                </div>
              </div>
              <div class="dm-header-actions">
                <template v-if="docModal.docs.length > 1">
                  <button class="dm-nav-btn" @click="docModal.active = (docModal.active - 1 + docModal.docs.length) % docModal.docs.length">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span class="dm-counter">{{ docModal.active + 1 }} / {{ docModal.docs.length }}</span>
                  <button class="dm-nav-btn" @click="docModal.active = (docModal.active + 1) % docModal.docs.length">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  <div class="dm-divider"></div>
                </template>
                <a :href="docModal.docs[docModal.active]?.src" target="_blank" class="dm-btn dm-btn--primary">Open ↗</a>
                <a :href="docModal.docs[docModal.active]?.src" :download="docModal.docs[docModal.active]?.title || 'document'" class="dm-btn dm-btn--ghost">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download
                </a>
                <button class="dm-close" @click="closeDocModal">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            <!-- Body: doc-list sidebar + big viewer -->
            <div class="dm-body">
              <div class="dm-sidebar" v-if="docModal.docs.length > 1">
                <button
                  v-for="(doc, i) in docModal.docs"
                  :key="i"
                  class="dm-side-item"
                  :class="{ 'dm-side-item--active': docModal.active === i }"
                  @click="docModal.active = Number(i)"
                >
                  <div class="dm-side-icon">
                    <img v-if="isImage(doc)" :src="doc.src" :alt="doc.title" class="dm-side-thumb" />
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div class="dm-side-text">
                    <span class="dm-side-title">{{ doc.title || 'Document ' + (Number(i) + 1) }}</span>
                    <span class="dm-side-sub">{{ getExt(doc) }}</span>
                  </div>
                </button>
              </div>
              <div class="dm-viewer">
                <img
                  v-if="docModal.docs[docModal.active] && isImage(docModal.docs[docModal.active])"
                  :src="docModal.docs[docModal.active].src"
                  :alt="docModal.docs[docModal.active].title"
                  class="dm-viewer-img"
                />
                <iframe
                  v-else-if="docModal.docs[docModal.active]"
                  :src="docModal.docs[docModal.active].src"
                  class="dm-viewer-iframe"
                  frameborder="0"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '../../api';
import { DateTime } from 'luxon';
import { message } from '../../utils/message';
import { useConfigStore } from '../../store/configStore';

const config    = useConfigStore();
const router    = useRouter();
const loading   = ref(true);
const error     = ref('');
const page      = ref(1);
const total     = ref(0);
const limit     = 10;

// Order History filters (server-side): approval state, delivery status, dates.
const approvalFilter = ref('');   // '' | Pending | Approved | Rejected
const deliveryFilter = ref('');   // '' | Placed | Intransit | Delivered | Invoiced | Cancelled
const approvalEnabled = ref(false); // does order-approval apply to this customer (from API)
const onFilterChange = () => {
  page.value = 1;
  expandedRows.clear();
  fetchOrders();
};

/* ── Date range ───────────────────────────────────────────────────────────────
 * Ranges the DATE column, which is when the order was placed. Filtering on the
 * delivery date while the visible column shows a different one would read as a
 * bug; the delivery date is carried in the export instead.
 *
 * Presets first, because almost nobody wants to pick two arbitrary days -- they
 * want this month, or last month. The custom pair is there for the ones who do.
 */
// luxon exports DateTime as a namespace here, so the instance type is derived
// from the factory rather than named directly.
const isoDay = (d: ReturnType<typeof DateTime.now>): string => d.toFormat('yyyy-MM-dd');
const today = isoDay(DateTime.now());

const datePresets: { key: string; label: string; range: () => [string, string] | null }[] = [
  { key: 'all',       label: 'All time',   range: () => null },
  { key: 'today',     label: 'Today',      range: () => [today, today] },
  { key: 'yesterday', label: 'Yesterday',  range: () => { const d = DateTime.now().minus({ days: 1 }); return [isoDay(d), isoDay(d)]; } },
  { key: 'week',      label: 'This week',  range: () => [isoDay(DateTime.now().startOf('week')), today] },
  { key: 'last7',     label: 'Last 7 days', range: () => [isoDay(DateTime.now().minus({ days: 6 })), today] },
  { key: 'month',     label: 'This month', range: () => [isoDay(DateTime.now().startOf('month')), today] },
  { key: 'lmonth',    label: 'Last month', range: () => { const m = DateTime.now().minus({ months: 1 }); return [isoDay(m.startOf('month')), isoDay(m.endOf('month'))]; } },
  { key: 'quarter',   label: 'This quarter', range: () => [isoDay(DateTime.now().startOf('quarter')), today] },
  { key: 'year',      label: 'This year',  range: () => [isoDay(DateTime.now().startOf('year')), today] },
];

const dateFrom     = ref('');
const dateTo       = ref('');
const activePreset = ref('all');
const customFrom   = ref('');
const customTo     = ref('');
const dateOpen     = ref(false);

const prettyDay = (iso: string): string => (iso ? DateTime.fromISO(iso).toFormat('d LLL yyyy') : '');

const dateLabel = computed(() => {
  if (!dateFrom.value && !dateTo.value) return 'All time';
  const preset = datePresets.find((p) => p.key === activePreset.value);
  if (preset && preset.key !== 'custom' && preset.key !== 'all') return preset.label;
  if (dateFrom.value && dateTo.value) {
    return dateFrom.value === dateTo.value
      ? prettyDay(dateFrom.value)
      : `${prettyDay(dateFrom.value)} – ${prettyDay(dateTo.value)}`;
  }
  return dateFrom.value ? `From ${prettyDay(dateFrom.value)}` : `Up to ${prettyDay(dateTo.value)}`;
});

const dateActive = computed(() => Boolean(dateFrom.value || dateTo.value));

const applyPreset = (preset: typeof datePresets[number]) => {
  const range = preset.range();
  activePreset.value = preset.key;
  dateFrom.value = range ? range[0] : '';
  dateTo.value   = range ? range[1] : '';
  customFrom.value = dateFrom.value;
  customTo.value   = dateTo.value;
  dateOpen.value = false;
  onFilterChange();
};

const applyCustom = () => {
  // Tolerate a range entered backwards rather than refusing it.
  let from = customFrom.value;
  let to   = customTo.value;
  if (from && to && from > to) [from, to] = [to, from];
  dateFrom.value = from;
  dateTo.value   = to;
  activePreset.value = 'custom';
  dateOpen.value = false;
  onFilterChange();
};

const clearDates = () => applyPreset(datePresets[0]);

const closeDatePop = () => { dateOpen.value = false; };

// tableData kept for template compatibility
const tableData = reactive({ data: <any>[] });
const expandedRows = reactive(new Set<any>());
const openDocs     = reactive(new Set<any>());

// The popover closes on any click outside it. Registered once here rather than
// with a directive, to keep the component free of extra dependencies.
onMounted(() => document.addEventListener('click', closeDatePop));
onUnmounted(() => document.removeEventListener('click', closeDatePop));

const fetchOrders = async () => {
  loading.value = true;
  tableData.data = [];
  try {
    const params: any = { page: page.value, limit };
    if (approvalFilter.value) params.approval_status = approvalFilter.value;
    if (deliveryFilter.value) params.status = deliveryFilter.value;
    if (dateFrom.value) params.from = dateFrom.value;
    if (dateTo.value)   params.to   = dateTo.value;
    const res = await api.get('customer/orders', { params });
    const rows = res.data.data?.orders ?? res.data.data ?? [];
    rows.forEach((el: any) => tableData.data.push(el));
    total.value = res.data.data?.total ?? rows.length;
    approvalEnabled.value = res.data.data?.approval_enabled ?? false;
  } catch {
    error.value = 'Failed to load orders.';
    message.error('Failed to load orders.');
  } finally {
    loading.value = false;
  }
};

const totalPages = () => Math.ceil(total.value / limit);

const changePage = (p: number) => {
  page.value = p;
  expandedRows.clear();
  fetchOrders();
};

const toggleRow = (row: any) => {
  if (expandedRows.has(row.id)) {
    expandedRows.delete(row.id);
  } else {
    expandedRows.add(row.id);
    if (!row.orderDetails) loadOrderDetails(row);
  }
};

const loadOrderDetails = async (row: any) => {
  try {
    const r = await api.get(`customer/orders/${row.id}`);
    const idx = tableData.data.findIndex((el: any) => el.id === row.id);
    tableData.data[idx].orderDetails = r.data.data;
  } catch {
    message.error('Error loading order details');
  }
};

const toggleDocs = (id: any) => {
  if (openDocs.has(id)) openDocs.delete(id);
  else openDocs.add(id);
};

// ── Customer Order Approval ──────────────────────────────────────────────
const rejectMode   = reactive(new Set<any>());
const rejectReason = reactive<Record<number, string>>({});
const acting       = reactive(new Set<any>());

const APPROVAL_LABELS: Record<string, string> = { Pending: 'Pending your approval', Approved: 'Approved', Rejected: 'Rejected' };
const approvalLabel = (s: string) => APPROVAL_LABELS[s] ?? s;
const APPROVAL_CLASSES: Record<string, string> = { Pending: 'oh-appr--pending', Approved: 'oh-appr--approved', Rejected: 'oh-appr--rejected' };
const approvalClass = (s: string) => APPROVAL_CLASSES[s] ?? '';

// Approval-thread rendering helpers
const APPR_ACTION_LABELS: Record<string, string> = { Requested: 'Requested approval', Resubmitted: 'Resubmitted', Rejected: 'Rejected', Approved: 'Approved' };
const apprActionLabel = (a: string) => APPR_ACTION_LABELS[a] ?? a;
const APPR_ACTION_COLORS: Record<string, string> = { Requested: '#0061eb', Resubmitted: '#0061eb', Rejected: '#dc2626', Approved: '#16a34a' };
const apprActionColor = (a: string) => APPR_ACTION_COLORS[a] ?? '#64748b';
const fmtApprTime = (ts: string) => {
  if (!ts) return '';
  const d = new Date(String(ts).replace(' ', 'T'));
  return isNaN(d.getTime()) ? ts : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const afterApprovalAction = () => { expandedRows.clear(); fetchOrders(); };

const approveOrder = async (row: any) => {
  if (acting.has(row.id)) return;
  acting.add(row.id);
  try {
    await api.post(`customer/orders/${row.id}/approve`);
    message.success('Order approved');
    afterApprovalAction();
  } catch (e: any) {
    message.error(e?.response?.data?.message || 'Could not approve the order.');
  } finally { acting.delete(row.id); }
};
const startReject  = (row: any) => { rejectMode.add(row.id); if (!(row.id in rejectReason)) rejectReason[row.id] = ''; };
const cancelReject = (row: any) => { rejectMode.delete(row.id); };
const submitReject = async (row: any) => {
  const reason = (rejectReason[row.id] || '').trim();
  if (!reason) { message.warning('Please give a reason for rejecting.'); return; }
  if (acting.has(row.id)) return;
  acting.add(row.id);
  try {
    await api.post(`customer/orders/${row.id}/reject`, { reason });
    message.success('Order rejected');
    rejectMode.delete(row.id);
    afterApprovalAction();
  } catch (e: any) {
    message.error(e?.response?.data?.message || 'Could not reject the order.');
  } finally { acting.delete(row.id); }
};

const fmtOrderNum = (n: string) => {
  if (!n) return n;
  // Already new format: "ORD 2604-00018"
  if (/^ORD \d{4}-\d+$/.test(n)) return n;
  // Old format: "ORD-202604020017" → extract YYMM + last 4 digits as counter
  const m = n.match(/ORD-(\d{4})(\d{2})\d{2}(\d{1,4})$/);
  if (m) return `ORD ${m[1].slice(2)}${m[2]}-${m[3].padStart(5, '0')}`;
  return n;
};

const transformDate  = (d: string) => {
  if (!d) return '—';
  // delivery_logs timestamps arrive as space-separated SQL datetimes; fromISO
  // rejects those (→ literal "Invalid DateTime"), so fall back to fromSQL.
  let dt = DateTime.fromISO(d, { zone: 'UTC' });
  if (!dt.isValid) dt = DateTime.fromSQL(d, { zone: 'UTC' });
  return dt.isValid ? dt.toFormat('dd MMM yyyy, HH:mm') : '—';
};
const transformDateSQL = (d: string) => {
  if (!d) return '—';
  let dt = DateTime.fromISO(d, { zone: 'UTC' });
  if (!dt.isValid) dt = DateTime.fromSQL(d, { zone: 'UTC' });
  return dt.isValid ? dt.toFormat('dd MMM yyyy, HH:mm') : '—';
};

// Classify by the status text (unambiguous) rather than the numeric code — the old
// numeric thresholds mislabelled Delivered/Invoiced as blue "active" and, worse,
// painted Cancelled with the green "delivered" badge.
const getStatusClass = (statusText: string) => {
  switch ((statusText || '').toLowerCase()) {
    case 'placed':    return 'oh-badge--default';   // received, awaiting pickup
    case 'intransit': return 'oh-badge--active';
    case 'delivered':
    case 'invoiced':  return 'oh-badge--delivered';
    case 'cancelled': return 'oh-badge--cancelled';
    default:          return 'oh-badge--default';
  }
};

// Humanise the raw status text — the API emits "Intransit" (no space); show "In transit".
const STATUS_LABELS: Record<string, string> = {
  Placed:    'Placed',
  Intransit: 'In transit',
  Delivered: 'Delivered',
  Invoiced:  'Invoiced',
  Cancelled: 'Cancelled',
};
const statusLabel = (s: string) => STATUS_LABELS[s] ?? s;

const returnOrderTrackingLink  = (id: number) => `/track/${id}`;

const formatDistance = (_text: string | null | undefined, km: number | null | undefined): string => {
  return km ? `${Math.ceil(Number(km))} km` : '';
};

// Returns a normalised stops array: [{location, isPickup}]
// Prefers order.stops from the API; falls back to paths (tracking data).
const getRouteStops = (row: any): { id?: any; location: string; status?: string }[] => {
  const stops = row.orderDetails?.order?.stops as any[] | undefined;
  if (stops && stops.length > 0) {
    return stops.map((s: any) => ({
      id: s.id,
      location: s.location || s.location_json ? (() => { try { return JSON.parse(s.location_json ?? '{}').address || s.location; } catch { return s.location; } })() : '',
      status: s.status,
    }));
  }
  // Fall back to paths tracking data
  const paths = (row.orderDetails?.paths ?? []) as any[];
  if (paths.length > 0) return paths.map((p: any) => ({ location: p.name || '' }));
  // Last resort: pickup + dropoff from row
  return [{ location: row.pickup || '' }, { location: row.dropoff || '' }];
};
// New API shape: order.documents[] = [{ id, doc_type, mime_type, original_name, url, ... }].
// Normalise into the gallery/modal's expected { src, title, mime } shape.
const getOrderDocuments = (orderDetails: any): any[] => orderDetails?.order?.documents ?? [];
const hasDeliveryDocuments = (orderDetails: any) => getOrderDocuments(orderDetails).length > 0;
// approved: an approved image arrives with the stamp already drawn into it, but a
// PDF cannot carry one — so the viewer says it in words as well.
const extractDocumentsInfo = (orderDetails: any) => getOrderDocuments(orderDetails).map((doc: any) => ({
  src:      doc.url,
  title:    doc.doc_type || doc.original_name,
  mime:     doc.mime_type || '',
  name:     doc.original_name || '',
  approved: !!doc.approved,
}));
const toDocInfo = (doc: any) => ({ src: doc.url, title: doc.doc_type || doc.original_name, mime: doc.mime_type || '', name: doc.original_name || '', approved: !!doc.approved });
// Delivery-point documents for one destination (matched by stop_id).
const stopDocuments = (row: any, stopId: any): any[] =>
  (row?.orderDetails?.order?.documents ?? []).filter((d: any) => Number(d.stop_id) === Number(stopId)).map(toDocInfo);
// Order-level documents (Order List, Manifest, …).
const orderLevelDocuments = (row: any): any[] =>
  (row?.orderDetails?.order?.order_documents ?? []).map(toDocInfo);
// Mime-first image test — presigned Spaces URLs carry a ?X-Amz-... query string, so a
// bare extension match on the URL would mis-classify images as files.
const isImage  = (doc: any) => {
  if (typeof doc === 'object' && doc) {
    if (/^image\//i.test(doc.mime || '')) return true;
    return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(doc.src || doc.name || '');
  }
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(String(doc));
};
const getExt   = (doc: any) => {
  const ref = (typeof doc === 'object' && doc) ? (doc.name || doc.src || '') : String(doc);
  const m = ref?.match(/\.([a-z0-9]+)(?:\?|$)/i);
  if (m) return m[1].toUpperCase();
  const mime = (typeof doc === 'object' && doc?.mime) ? doc.mime.split('/')[1] : '';
  return mime ? mime.toUpperCase() : 'FILE';
};

const docModal = reactive({ open: false, docs: <any>[], active: 0, orderNumber: '' });
const openDocModal  = (docs: any[], index: number, row: any) => { docModal.docs = docs; docModal.active = index; docModal.orderNumber = fmtOrderNum(row.order_number); docModal.open = true; };
const closeDocModal = () => { docModal.open = false; };

/* ── Export ───────────────────────────────────────────────────────────────────
 * Asks the server, which is the whole point of the change. This used to
 * serialise `tableData.data` in the browser -- one page, ten rows -- so a
 * customer with four hundred orders clicked CSV and got ten of them. Nobody
 * noticed at ten a page; with a date filter on the screen it becomes plainly
 * wrong: pick a quarter, export, receive ten rows.
 *
 * The same filters go up, so the file always matches what is on screen.
 */
const exporting = ref('');   // '' | 'csv' | 'pdf' -- disables just the one button

const downloadExport = async (format: 'csv' | 'pdf') => {
  if (exporting.value) return;
  exporting.value = format;
  try {
    const params: any = { format };
    if (approvalFilter.value) params.approval_status = approvalFilter.value;
    if (deliveryFilter.value) params.status = deliveryFilter.value;
    if (dateFrom.value) params.from = dateFrom.value;
    if (dateTo.value)   params.to   = dateTo.value;

    const res = await api.get('customer/orders/export', { params, responseType: 'blob' });

    // An error still arrives as a Blob because of responseType, so a failed
    // export would otherwise "succeed" and save a file full of JSON.
    const type = String(res.data?.type ?? '');
    if (type.includes('json') || type.includes('html')) {
      throw new Error('the server returned an error instead of a file');
    }

    const stamp = DateTime.now().toFormat('yyyy-LL-dd');
    const name  = `orders-${stamp}.${format}`;
    const url   = URL.createObjectURL(res.data);
    const link  = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Freed on the next tick; revoking immediately cancels the download in Safari.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e: any) {
    message.error('That export did not come through. Please try again.');
  } finally {
    exporting.value = '';
  }
};

onMounted(fetchOrders);

// Refresh when the tab regains focus so an open list reflects ops-side status changes
// (e.g. an order delivered after the page was loaded) without manual reload.
const refreshOnFocus = () => { if (document.visibilityState === 'visible') fetchOrders(); };
onMounted(() => document.addEventListener('visibilitychange', refreshOnFocus));
onUnmounted(() => document.removeEventListener('visibilitychange', refreshOnFocus));
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
* { box-sizing: border-box; }

.oh-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; overflow-x: hidden; }
.oh-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

/* ── Header ─────────────────────────────── */
.oh-header { background: white; padding: 24px 0 0; }
.oh-header-inner {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 18px;
}
.oh-title { font-size: 1.4rem; font-weight: 800; color: #0f1729; margin: 0; letter-spacing: -0.02em; }
.oh-header-actions { display: flex; gap: 8px; }
.oh-btn {
  height: 38px; padding: 0 16px; border-radius: 8px; font-family: 'DM Sans', sans-serif;
  font-size: 0.82rem; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s;
  white-space: nowrap; display: inline-flex; align-items: center; gap: 7px;
}
.oh-btn--purple { width: 130px; justify-content: center; background: white; color: #7c3aed; border: 1.5px solid #a78bfa; }
.oh-btn--purple:hover { background: #7c3aed; color: white; border-color: #7c3aed; }
.oh-btn--purple:active { background: #6d28d9; border-color: #6d28d9; color: white; }
.oh-btn--csv { width: 130px; justify-content: center; background: white; color: #16a34a; border: 1.5px solid #22c55e; }
.oh-btn--csv:hover { background: #22c55e; color: white; border-color: #22c55e; }
.oh-btn--csv:active { background: #15803d; border-color: #15803d; color: white; }
.oh-btn--navy { background: linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%); color: white; }
.oh-btn--navy:hover { opacity: 0.92; }

/* ── List wrapper ──────────────────────── */
.oh-list-wrap { padding-top: 4px; padding-bottom: 48px; }

/* ── Table ─────────────────────────────── */
.oh-table {
  width: 100%;
  border: 1.5px solid #ccdeff;
  border-radius: 12px;
  overflow: hidden;
}

/* Header row */
.oh-thead {
  display: grid;
  grid-template-columns: 132px 104px 1fr 132px 104px 1fr 1fr 116px 80px;
  background: #f0f6ff;
  border-bottom: 1.5px solid #ccdeff;
  padding: 0 20px;
}
.oh-th {
  font-size: 0.68rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.08em;
  padding: 12px 10px; white-space: nowrap;
}
.oh-th--amount { text-align: right; }
.oh-th--action { text-align: right; }

/* Body */
.oh-tbody { display: flex; flex-direction: column; }

/* Row wrap */
.oh-row-wrap { border-bottom: 1px solid #e8f0ff; }
.oh-row-wrap:last-child { border-bottom: none; }
.oh-row-wrap--open { background: white; }

/* Approval column present → insert one column after STATUS */
.oh-table.oh-has-approval .oh-thead,
.oh-table.oh-has-approval .oh-row {
  grid-template-columns: 126px 96px 104px 1fr 122px 98px 1fr 1fr 110px 76px;
}

/* Data row */
.oh-row {
  display: grid;
  grid-template-columns: 132px 104px 1fr 132px 104px 1fr 1fr 116px 80px;
  align-items: center;
  padding: 0 20px;
  height: 56px;
  cursor: pointer;
  transition: background 0.12s;
}
.oh-row:hover { background: #f0f6ff; }
.oh-row-wrap--open .oh-row { background: #f0f6ff; }

/* Cells */
.oh-td { padding: 0 10px; overflow: hidden; }

.oh-ref {
  font-size: 0.84rem; font-weight: 700; color: #0061eb;
  letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap;
}

.oh-td-text {
  font-size: 0.83rem; color: #243558; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
  text-transform: capitalize;
}

.oh-td-muted {
  font-size: 0.81rem; color: #4a5f82; font-weight: 400;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
}

.oh-td-amount {
  font-size: 0.84rem; color: #0061eb; font-weight: 700;
  text-align: right; display: block; white-space: nowrap;
}

/* Status badge — pill outline style matching the reference */
.oh-badge {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 20px;
  font-size: 0.74rem; font-weight: 600; white-space: nowrap;
  border: 1.5px solid currentColor;
}
.oh-badge--pending   { color: #dc2626; background: #fef2f2; border-color: #fca5a5; }
.oh-badge--active    { color: #2563eb; background: #eff6ff; border-color: #93c5fd; }
.oh-badge--delivered { color: #16a34a; background: #f0fdf4; border-color: #86efac; }
.oh-badge--cancelled { color: #6b7280; background: #f3f4f6; border-color: #d1d5db; }
.oh-badge--default   { color: #4a5f82; background: #f0f6ff; border-color: #b8d0f8; }

/* ── Filters (approval + delivery status) ─────────── */
.oh-filters { display: flex; gap: 16px; margin: 4px 0 14px; flex-wrap: wrap; }
.oh-filter { display: flex; flex-direction: column; gap: 5px; }
.oh-filter-lbl { font-size: 0.68rem; font-weight: 700; color: #6b86b8; text-transform: uppercase; letter-spacing: 0.06em; }
.oh-select {
  height: 38px; min-width: 180px; padding: 0 34px 0 12px;
  border: 1.5px solid #ccdeff; border-radius: 8px; background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234a5f82' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center;
  font-family: 'DM Sans', sans-serif; font-size: 0.84rem; font-weight: 600; color: #243558;
  cursor: pointer; -webkit-appearance: none; -moz-appearance: none; appearance: none;
}
.oh-select:focus { outline: none; border-color: #0061eb; }

/* ── Approval box (expanded card) ──────── */
.oh-appr-thread { margin: 10px 0 4px; display: flex; flex-direction: column; gap: 8px; max-height: 260px; overflow-y: auto; }
.oh-appr-msg { max-width: 88%; border: 1px solid #e5e9f0; background: #fff; border-radius: 12px; padding: 8px 11px; }
.oh-appr-msg--them { align-self: flex-start; }
.oh-appr-msg--me   { align-self: flex-end; background: #eff6ff; }
.oh-appr-msg-head { display: flex; align-items: center; gap: 6px; margin-bottom: 2px; flex-wrap: wrap; }
.oh-appr-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.oh-appr-who { font-size: 0.72rem; font-weight: 700; color: #334155; }
.oh-appr-act { font-size: 0.62rem; font-weight: 700; padding: 1px 6px; border-radius: 5px; }
.oh-appr-note { font-size: 0.8rem; color: #475569; margin: 0; line-height: 1.45; white-space: pre-line; }
.oh-appr-time { font-size: 0.62rem; color: #94a3b8; margin: 3px 0 0; }

.oh-appr-box { margin-top: 12px; padding: 12px; border-radius: 10px; border: 1.5px solid #e8f0ff; background: #f8fbff; }
.oh-appr-box--pending  { border-color: #fcd34d; background: #fffbeb; }
.oh-appr-box--approved { border-color: #86efac; background: #f0fdf4; }
.oh-appr-box--rejected { border-color: #fca5a5; background: #fef2f2; }
.oh-appr-pill { display: inline-flex; padding: 3px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 700; border: 1.5px solid currentColor; }
.oh-appr--pending  { color: #b45309; background: #fef3c7; border-color: #fcd34d; }
.oh-appr--approved { color: #15803d; background: #dcfce7; border-color: #86efac; }
.oh-appr--rejected { color: #b91c1c; background: #fee2e2; border-color: #fca5a5; }
.oh-appr-hint { font-size: 0.76rem; color: #6b7280; margin: 8px 0; }
.oh-appr-reason { font-size: 0.78rem; color: #7f1d1d; margin: 8px 0 0; line-height: 1.4; }
.oh-appr-textarea { width: 100%; margin: 8px 0; padding: 8px 10px; border: 1.5px solid #ccdeff; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.8rem; resize: vertical; }
.oh-appr-actions { display: flex; gap: 8px; margin-top: 8px; }
.oh-appr-btn { height: 34px; padding: 0 16px; border-radius: 8px; border: none; font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 700; cursor: pointer; transition: opacity 0.15s; }
.oh-appr-btn:disabled { opacity: 0.5; cursor: default; }
.oh-appr-btn--approve { background: #16a34a; color: white; }
.oh-appr-btn--reject  { background: #dc2626; color: white; }
.oh-appr-btn--ghost   { background: white; color: #4a5f82; border: 1.5px solid #ccdeff; }

/* View link */
.oh-view-link {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.8rem; font-weight: 600; color: #2563eb;
  float: right; white-space: nowrap; transition: gap 0.15s;
}
.oh-row:hover .oh-view-link { gap: 7px; }

/* ── Expanded detail ────────────────────── */
.oh-detail {
  border-top: 1.5px solid #ccdeff;
  background: #edf3ff;
  padding: 20px 20px 16px;
}
.oh-detail-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; align-items: stretch; }
.oh-dcol { display: flex; flex-direction: column; gap: 16px; min-width: 0; height: 100%; }
/* In the driver column, the driver card grows so its bottom aligns with the other columns;
   an Order Documents card (when present) stays compact at the bottom. */
.oh-dcol > .oh-dcard:first-child { flex: 1 1 auto; }

.oh-dcard {
  background: white; border-radius: 12px;
  border: 1.5px solid #ccdeff; padding: 16px 20px;
  display: flex; flex-direction: column;
}
.oh-dcard-head { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
.oh-dcard-icon {
  width: 28px; height: 28px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.oh-dcard-icon--blue   { background: #eff6ff; }
.oh-dcard-icon--purple { background: #f5f3ff; }
.oh-dcard-icon--teal   { background: #f0fdfa; }
.oh-dcard-label { font-size: 0.72rem; font-weight: 700; color: #6b86b8; text-transform: uppercase; letter-spacing: 0.07em; }

.oh-dkv-list { display: flex; flex-direction: column; gap: 0; }
.oh-dkv {
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  padding: 8px 0; border-bottom: 1px solid #e8f0ff;
}
.oh-dkv:last-child { border-bottom: none; padding-bottom: 0; }
.oh-dk { font-size: 0.78rem; color: #4a5f82; font-weight: 500; flex-shrink: 0; }
.oh-dv { font-size: 0.83rem; color: #0f1729; font-weight: 600; text-align: right; }
.oh-dv--navy { color: #0061eb; font-weight: 700; }
.oh-dv--green { color: #15803d; font-weight: 600; }

/* Route */
.oh-stop { display: flex; gap: 10px; }
.oh-stop-track { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; padding-top: 4px; }
.oh-stop-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.oh-stop-dot--green { background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
.oh-stop-dot--amber { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }
.oh-stop-dot--mid { background: #94a3b8; }
.oh-stop-line { width: 2px; flex: 1; min-height: 10px; background: #ccdeff; margin: 2px 0; }
.oh-stop-text { flex: 1; padding-bottom: 6px; }
.oh-stop-type {
  font-size: 0.62rem; color: #6b86b8; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
  display: flex; align-items: center; gap: 4px;
}
.oh-stop-type--drop { color: #4a5f82; }
.oh-stop-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 13px; height: 13px; border-radius: 50%;
  background: #e8f0ff; color: #3d5073;
  font-size: 0.58rem; font-weight: 700; flex-shrink: 0;
}
.oh-stop-name { font-size: 0.78rem; color: #0f1729; font-weight: 500; margin-top: 1px; line-height: 1.3; }

/* Pricing breakdown */
.oh-pricing { margin-top: 12px; padding-top: 10px; border-top: 1px solid #e8f0ff; }
.oh-pricing-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 7px; }
.oh-pricing-label { font-size: 0.68rem; font-weight: 700; color: #6b86b8; text-transform: uppercase; letter-spacing: 0.07em; }
.oh-pricing-model { font-size: 0.68rem; font-weight: 700; color: #2563eb; background: #eff6ff; padding: 2px 8px; border-radius: 20px; }
.oh-pline { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; padding: 3px 0; font-size: 0.75rem; color: #3d5073; border-bottom: 1px solid #f0f6ff; }
.oh-pline span:last-child { font-weight: 600; color: #0f1729; white-space: nowrap; }
.oh-pline--total { border-top: 1.5px solid #ccdeff; border-bottom: none; margin-top: 3px; padding-top: 6px; }
.oh-pline--total span { font-weight: 700 !important; color: #0061eb !important; font-size: 0.8rem; }
/* ── Route card redesign ─────────────────── */
.oh-dcard--route { display: flex; flex-direction: column; }

/* Distance badge in header */
.oh-route-dist-badge {
  display: inline-flex; align-items: center; gap: 4px; margin-left: auto;
  background: #f5f3ff; color: #7c3aed; border-radius: 20px;
  padding: 3px 10px; font-size: 0.71rem; font-weight: 700;
}

/* Stop list */
.oh-route-list { display: flex; flex-direction: column; gap: 0; flex: 1; }
.oh-ritem { display: flex; gap: 12px; }
.oh-ritem-left { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; padding-top: 3px; }
.oh-ritem-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.oh-ritem-dot--green { background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
.oh-ritem-dot--amber { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }
.oh-ritem-dot--mid   { background: #b8d0f8; }
.oh-ritem-line { width: 2px; flex: 1; min-height: 12px; background: #ccdeff; margin: 3px 0; }
.oh-ritem-body { flex: 1; padding-bottom: 10px; }
.oh-ritem-tag {
  font-size: 0.61rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.06em;
  display: flex; align-items: center; gap: 4px; margin-bottom: 1px;
}
.oh-ritem-tag--drop { color: #4a5f82; }
.oh-ritem-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 14px; height: 14px; border-radius: 50%;
  background: #e8f0ff; color: #3d5073;
  font-size: 0.58rem; font-weight: 700; flex-shrink: 0;
}
.oh-ritem-addr { font-size: 0.8rem; color: #0f1729; font-weight: 500; line-height: 1.35; }

/* Distance row */
.oh-route-distance {
  display: flex; align-items: center; gap: 7px;
  background: #f5f3ff; border-radius: 8px; padding: 7px 12px;
  margin: 10px 0 0;
}
.oh-route-distance-label { font-size: 0.73rem; font-weight: 500; color: #7c3aed; flex: 1; }
.oh-route-distance-value { font-size: 0.8rem; font-weight: 700; color: #5b21b6; }

/* Route footer: stops count + track button */
.oh-route-footer {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 8px; padding-top: 10px; border-top: 1px solid #e8f0ff;
  gap: 8px;
}
.oh-route-stops-pill {
  display: inline-flex; align-items: center; gap: 4px;
  background: #e8f0ff; border-radius: 20px; padding: 3px 9px;
  font-size: 0.71rem; font-weight: 600; color: #4a5f82;
}
.oh-track-btn {
  display: inline-flex; align-items: center; gap: 5px;
  background: #eff6ff; color: #2563eb; border-radius: 8px;
  padding: 5px 12px; font-size: 0.78rem; font-weight: 600;
  text-decoration: none; transition: background 0.15s; white-space: nowrap;
}
.oh-track-btn:hover { background: #dbeafe; color: #1d4ed8; }

.oh-route-stats { display: flex; gap: 6px; flex-wrap: wrap; margin: 8px 0; }
.oh-route-chip {
  display: inline-flex; align-items: center; gap: 4px;
  background: #e8f0ff; border-radius: 20px; padding: 3px 9px;
  font-size: 0.72rem; font-weight: 600; color: #3d5073;
}
.oh-detail-actions { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.oh-track-link { font-size: 0.81rem; font-weight: 600; color: #2563eb; text-decoration: none; }
.oh-track-link:hover { color: #1d4ed8; }
.oh-docs-section { margin-top: 12px; border-top: 1px solid #e8f0ff; padding-top: 10px; }

.oh-docs-toggle {
  display: flex; align-items: center; gap: 6px;
  background: none; border: none; padding: 0; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 600;
  color: #3d5073; transition: color 0.15s; width: 100%;
}
.oh-docs-toggle:hover { color: #0061eb; }
.oh-docs-count {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  background: #ccdeff; color: #3d5073;
  font-size: 0.65rem; font-weight: 700;
}
.oh-docs-chevron { margin-left: auto; color: #6b86b8; transition: transform 0.2s; }
.oh-docs-chevron--open { transform: rotate(180deg); }

.oh-docs-gallery {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px; margin-top: 10px;
}
.oh-doc-thumb {
  text-decoration: none; display: flex; flex-direction: column; gap: 5px;
  cursor: pointer;
}
.oh-doc-img-wrap {
  position: relative; border-radius: 8px; overflow: hidden;
  aspect-ratio: 1; background: #e8f0ff;
  border: 1.5px solid #ccdeff; transition: border-color 0.15s;
}
.oh-doc-thumb:hover .oh-doc-img-wrap { border-color: #2563eb; }
.oh-doc-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.oh-doc-overlay {
  position: absolute; inset: 0; background: rgba(0,97,235,0.55);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.18s;
}
.oh-doc-thumb:hover .oh-doc-overlay { opacity: 1; }
.oh-doc-label {
  font-size: 0.68rem; color: #4a5f82; font-weight: 500;
  text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* docs expand transition */
.docs-expand-enter-active, .docs-expand-leave-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); overflow: hidden; }
.docs-expand-enter-from, .docs-expand-leave-to { opacity: 0; max-height: 0; }
.docs-expand-enter-to, .docs-expand-leave-from { opacity: 1; max-height: 400px; }

/* Driver */
.oh-driver { display: flex; align-items: center; gap: 12px; }
.oh-driver-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 8px; min-height: 120px; padding: 14px 8px; }
.oh-driver-empty-icon { width: 44px; height: 44px; border-radius: 50%; background: #f3f4f6; display: flex; align-items: center; justify-content: center; }
.oh-driver-empty-title { font-size: 0.85rem; font-weight: 600; color: #6b7280; }
.oh-driver-empty-sub { font-size: 0.76rem; color: #9ca3af; line-height: 1.4; max-width: 190px; }

/* ── Document chips (per-destination + order documents) ── */
.oh-stopdocs { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
.oh-orderdocs { margin-top: 18px; padding-top: 14px; border-top: 1px solid #e8f0ff; }
.oh-orderdocs-title { font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; margin-bottom: 9px; }
.oh-orderdocs-list { display: flex; flex-direction: column; gap: 7px; }
.oh-doc-chip {
  display: inline-flex; align-items: center; gap: 6px; max-width: 100%;
  padding: 5px 10px; border: 1px solid #ccdeff; border-radius: 8px;
  background: #f0f6ff; color: #0061eb; font-family: inherit; font-size: 0.72rem;
  font-weight: 600; cursor: pointer; transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.oh-doc-chip:hover { background: #0061eb; color: #fff; border-color: #0061eb; }
.oh-doc-chip svg { flex-shrink: 0; }
.oh-doc-chip span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.oh-doc-chip--wide { width: 100%; justify-content: flex-start; }
.oh-driver-avatar {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #0061eb 0%, #003a96 100%);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 1rem; font-weight: 700;
}
.oh-driver-name { font-size: 0.86rem; font-weight: 600; color: #0061eb; }
.oh-driver-plate {
  display: inline-block; background: #e8f0ff; border-radius: 4px;
  padding: 2px 8px; font-size: 0.71rem; font-weight: 600;
  color: #3d5073; letter-spacing: 0.06em; margin-top: 4px;
}

/* Transition */
.expand-enter-active, .expand-leave-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); overflow: hidden; }
.expand-enter-from, .expand-leave-to { opacity: 0; max-height: 0; }
.expand-enter-to, .expand-leave-from { opacity: 1; max-height: 500px; }

/* Empty */
.oh-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; padding: 20px 0; }
.oh-page-btn { font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; padding: 8px 18px; border-radius: 8px; border: 1.5px solid #ccdeff; background: white; color: #0061eb; cursor: pointer; transition: border-color 0.15s; }
.oh-page-btn:hover:not(:disabled) { border-color: #0061eb; }
.oh-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.oh-page-info { font-family: 'DM Sans', sans-serif; font-size: 13px; color: #4a5f82; font-weight: 500; }
.oh-empty { text-align: center; padding: 60px 0; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.oh-empty-icon { font-size: 2.5rem; }
.oh-empty-text { font-size: 1rem; font-weight: 500; color: #6b86b8; }

/* ── Document Modal ──────────────────────── */
.dm-backdrop {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(10, 20, 40, 0.65);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.dm-modal {
  background: white; border-radius: 16px;
  width: 94vw; max-width: 1300px; height: 92vh; max-height: 92vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.3);
  overflow: hidden;
}
.dm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1.5px solid #e8f0ff; flex-shrink: 0;
}
.dm-header-left { display: flex; align-items: center; gap: 10px; }
.dm-header-icon {
  width: 34px; height: 34px; border-radius: 9px; background: #eff6ff;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.dm-title { font-size: 0.9rem; font-weight: 700; color: #0f1729; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 340px; }
.dm-subtitle { font-size: 0.75rem; color: #6b86b8; font-weight: 500; margin-top: 1px; letter-spacing: 0.04em; text-transform: uppercase; }
/* Says out loud what the stamp says on the image — the only signal a PDF gets. */
.dm-approved { color: #15803d; font-weight: 700; }
.dm-approved::before { content: '·'; color: #cbd5e1; font-weight: 500; margin: 0 5px; }
.dm-close {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: #e8f0ff; color: #4a5f82; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s; flex-shrink: 0;
}
.dm-close:hover { background: #fee2e2; color: #dc2626; }

.dm-body { display: flex; flex: 1; overflow: hidden; min-height: 0; }

/* Viewer */
.dm-viewer {
  flex: 1; min-width: 0; background: #e5e7eb; overflow: auto;
  display: flex; align-items: center; justify-content: center;
}
.dm-viewer-img { max-width: 100%; max-height: 100%; object-fit: contain; }
.dm-viewer-iframe { width: 100%; height: 100%; border: none; background: white; }
.dm-viewer-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; background: rgba(0,0,0,0.3); flex-shrink: 0;
}
.dm-doc-name { font-size: 0.8rem; color: rgba(255,255,255,0.8); font-weight: 500; }
.dm-open-btn {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.75rem; font-weight: 600; color: rgba(255,255,255,0.7);
  text-decoration: none; transition: color 0.15s;
}
.dm-open-btn:hover { color: white; }

/* Nav arrows */
.dm-nav {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: rgba(255,255,255,0.15); color: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s; backdrop-filter: blur(4px);
}
.dm-nav:hover { background: rgba(255,255,255,0.3); }
.dm-nav--prev { left: 12px; }
.dm-nav--next { right: 12px; }

/* Sidebar */
.dm-sidebar {
  width: 210px; flex-shrink: 0; border-right: 1.5px solid #eef2f7; background: #f8fafc;
  display: flex; flex-direction: column; gap: 4px; overflow-y: auto; padding: 8px;
}
.dm-header-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.dm-header-text { min-width: 0; }
.dm-nav-btn { width: 30px; height: 30px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
.dm-nav-btn:hover { background: #f8fafc; border-color: #cbd5e1; color: #334155; }
.dm-counter { font-size: 0.72rem; font-weight: 700; color: #64748b; min-width: 42px; text-align: center; }
.dm-divider { width: 1px; height: 20px; background: #e2e8f0; margin: 0 2px; }
.dm-btn { display: inline-flex; align-items: center; gap: 5px; font-size: 0.74rem; font-weight: 700; padding: 6px 12px; border-radius: 8px; text-decoration: none; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.dm-btn--primary { color: #0061eb; border: 1px solid #0061eb; background: #fff; }
.dm-btn--primary:hover { background: #0061eb; color: #fff; }
.dm-btn--ghost { color: #475569; border: 1px solid #cbd5e1; background: #fff; }
.dm-btn--ghost:hover { background: #f8fafc; border-color: #94a3b8; }
.dm-side-item { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; padding: 8px 9px; border: 1px solid transparent; border-radius: 9px; background: transparent; cursor: pointer; transition: all 0.15s; }
.dm-side-item:hover { background: #eef4ff; }
.dm-side-item--active { background: #e8f0ff; border-color: #93c5fd; }
.dm-side-icon { width: 34px; height: 34px; border-radius: 7px; background: #eef2f7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; }
.dm-side-thumb { width: 100%; height: 100%; object-fit: cover; }
.dm-side-text { min-width: 0; display: flex; flex-direction: column; }
.dm-side-title { font-size: 0.76rem; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dm-side-sub { font-size: 0.64rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; }

.dm-thumbs-label {
  font-size: 0.68rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.07em;
  padding: 12px 14px 8px;
}
.dm-thumbs {
  display: flex; flex-direction: column; gap: 4px;
  padding: 0 10px; overflow-y: auto; flex-shrink: 0; max-height: 240px;
}
.dm-thumb {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px; border-radius: 8px; cursor: pointer;
  transition: background 0.12s; border: 1.5px solid transparent;
}
.dm-thumb:hover { background: #f0f6ff; }
.dm-thumb--active { background: #eff6ff; border-color: #93c5fd; }
.dm-thumb-img { width: 36px; height: 36px; border-radius: 6px; object-fit: cover; flex-shrink: 0; border: 1px solid #ccdeff; }
.dm-thumb-file {
  width: 36px; height: 36px; border-radius: 6px; flex-shrink: 0;
  background: #e8f0ff; border: 1px solid #ccdeff;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
}
.dm-thumb-file span { font-size: 0.55rem; font-weight: 700; color: #4a5f82; letter-spacing: 0.02em; }
.dm-thumb-title { font-size: 0.75rem; color: #243558; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }

/* Downloads */
.dm-upload-section {
  padding: 12px 10px 16px; border-top: 1.5px solid #e8f0ff; flex: 1; overflow-y: auto;
}
.dm-upload-label {
  font-size: 0.68rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 8px; padding: 0 4px;
}
.dm-download-list { display: flex; flex-direction: column; gap: 4px; }
.dm-download-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 10px; border-radius: 9px;
  border: 1.5px solid #ccdeff; background: #f0f6ff;
  text-decoration: none; transition: all 0.15s; cursor: pointer;
}
.dm-download-item:hover { border-color: #0061eb; background: #eff6ff; }
.dm-download-item:hover .dm-download-arrow { color: #0061eb; transform: translateY(2px); }
.dm-download-icon {
  width: 30px; height: 30px; border-radius: 7px; background: white;
  border: 1.5px solid #ccdeff; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: #3d5073;
}
.dm-download-info { flex: 1; min-width: 0; }
.dm-download-name { display: block; font-size: 0.78rem; font-weight: 600; color: #0f1729; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dm-download-type { font-size: 0.67rem; color: #6b86b8; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
.dm-download-arrow { color: #6b86b8; transition: all 0.2s; flex-shrink: 0; }

/* Modal transitions */
.modal-fade-enter-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); }
.modal-fade-leave-active { transition: all 0.18s cubic-bezier(0.4,0,0.2,1); }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .dm-modal { transform: scale(0.96) translateY(8px); }
.modal-fade-enter-to .dm-modal { transform: scale(1) translateY(0); }


/* ── Date range filter ─────────────────────────────────────────────────────── */
.oh-filter--date { position: relative; }

.oh-datebtn {
  display: inline-flex; align-items: center; justify-content: space-between; gap: 10px;
  min-width: 168px; text-align: left; cursor: pointer;
  /* .oh-select paints a chevron as a background image for native selects. This
     is a real button with its own SVG chevron, so drop the painted one or it
     shows two, and reclaim the padding that was reserved for it. */
  background-image: none;
  padding: 0 12px;
}
.oh-datebtn-txt { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.oh-datebtn--on { border-color: #0061eb; color: #0061eb; font-weight: 600; }

.oh-datepop {
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 40;
  display: flex; background: #fff;
  border: 1px solid #ccdeff; border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 42, 92, 0.14);
  overflow: hidden;
}
.oh-datepop-presets {
  display: flex; flex-direction: column; padding: 6px;
  border-right: 1px solid #e8f0ff; background: #f8fbff; min-width: 138px;
}
.oh-preset {
  border: none; background: transparent; text-align: left; cursor: pointer;
  padding: 7px 10px; border-radius: 6px; font-size: 0.78rem; color: #334d7a;
  font-family: inherit; white-space: nowrap;
}
.oh-preset:hover { background: #e8f0ff; }
.oh-preset--on { background: #0061eb; color: #fff; font-weight: 600; }

.oh-datepop-custom { padding: 10px 12px 12px; min-width: 176px; }
.oh-datepop-hd {
  margin: 0 0 8px; font-size: 0.66rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.08em;
}
.oh-datepop-lbl {
  display: block; font-size: 0.68rem; font-weight: 600; color: #6b86b8; margin: 8px 0 3px;
}
.oh-dateinput {
  width: 100%; border: 1.5px solid #dbe7fb; border-radius: 7px;
  padding: 6px 8px; font-size: 0.78rem; font-family: inherit; color: #1f3358; outline: none;
}
.oh-dateinput:focus { border-color: #0061eb; }
.oh-datepop-actions { display: flex; gap: 8px; margin-top: 12px; }
.oh-datepop-btn {
  flex: 1; border: 1.5px solid #dbe7fb; background: #fff; color: #334d7a;
  border-radius: 7px; padding: 6px 8px; font-size: 0.75rem; font-weight: 600;
  font-family: inherit; cursor: pointer;
}
.oh-datepop-btn:hover { background: #f0f6ff; }
.oh-datepop-btn--go { background: #0061eb; border-color: #0061eb; color: #fff; }
.oh-datepop-btn--go:hover { background: #0052c9; }
.oh-datepop-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── Vehicle column ────────────────────────────────────────────────────────── */
.oh-td--veh { display: flex; flex-direction: column; justify-content: center; gap: 1px; line-height: 1.25; }
.oh-plate {
  font-size: 0.78rem; font-weight: 700; color: #1f3358;
  letter-spacing: 0.03em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.oh-veh-type {
  font-size: 0.68rem; color: #8095bd;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* ── PDF button ──────────────────────────────────────────────────────────────
   The full border shorthand, like its siblings: .oh-btn sets `border: none`, so
   setting only border-color leaves the button with no outline at all. */
.oh-btn--pdf { width: 130px; justify-content: center; background: white; color: #c2410c; border: 1.5px solid #fb923c; }
.oh-btn--pdf:hover:not(:disabled) { background: #fb923c; color: white; border-color: #fb923c; }
.oh-btn--pdf:active { background: #ea580c; border-color: #ea580c; color: white; }
.oh-btn:disabled { opacity: 0.6; cursor: not-allowed; }


/* Manifest number. Sits with the documents because that is what it references,
   and an order may carry more than one, joined with a semicolon by the API. */
.oh-manifest {
  display: flex; align-items: baseline; gap: 8px;
  margin: 0 0 10px; padding: 7px 10px;
  background: #f0f6ff; border: 1px solid #ddeaff; border-radius: 7px;
}
.oh-manifest-k {
  font-size: 0.66rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.07em; flex-shrink: 0;
}
.oh-manifest-v {
  font-size: 0.8rem; font-weight: 600; color: #1f3358;
  word-break: break-word; letter-spacing: 0.02em;
}

</style>